'use client';

import React from 'react';

import { Box, type BoxProps } from '../components/index-chakra';
import { useIsRTL } from '../providers/rtl-provider';
import { shouldFlipIcon, getMirrorTransform } from '../utils/rtl-utils';

export interface DirectionalIconProps extends BoxProps {
  /**
   * The icon component or element to render
   */
  icon: React.ElementType | React.ReactNode;
  /**
   * Whether the icon should flip in RTL mode
   * If not specified, will auto-detect based on icon name
   */
  shouldFlip?: boolean;
  /**
   * Size of the icon (width and height)
   */
  size?: string | number;
  /**
   * Additional transform to apply
   */
  transform?: string;
  /**
   * Icon props to pass to the icon component
   */
  iconProps?: Record<string, unknown>;
}

/**
 * DirectionalIcon - A wrapper component that automatically flips icons in RTL layouts
 *
 * @example
 * ```tsx
 * // Auto-detection based on icon name
 * <DirectionalIcon icon={FiArrowRight} />
 *
 * // Explicit flip control
 * <DirectionalIcon icon={FiPlay} shouldFlip={false} />
 *
 * // With custom props
 * <DirectionalIcon
 *   icon={FiChevronRight}
 *   size={24}
 *   color="#6B7280"
 * />
 * ```
 */
export function DirectionalIcon({
  icon: Icon,
  shouldFlip,
  size = '1em',
  transform,
  iconProps = {},
  ...boxProps
}: DirectionalIconProps) {
  const isRTL = useIsRTL();

  // Determine if icon should flip
  const shouldFlipThisIcon =
    shouldFlip !== undefined
      ? shouldFlip
      : typeof Icon === 'function' && Icon.name
        ? shouldFlipIcon(Icon.name)
        : false;

  // Calculate transform
  const mirrorTransform = getMirrorTransform(isRTL && shouldFlipThisIcon);
  const combinedTransform = [mirrorTransform, transform].filter(Boolean).join(' ');

  // Render icon
  const iconElement = React.isValidElement(Icon) ? (
    Icon
  ) : typeof Icon === 'function' ? (
    <Icon size={size} {...iconProps} />
  ) : null;

  if (!iconElement) {
    return null;
  }

  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={size}
      height={size}
      transform={combinedTransform || undefined}
      transition="transform 0.2s"
      {...boxProps}
    >
      {React.cloneElement(
        iconElement as React.ReactElement,
        {
          size,
          ...iconProps,
        } as Record<string, unknown>
      )}
    </Box>
  );
}

/**
 * Higher-order component to make any icon component direction-aware
 *
 * @example
 * ```tsx
 * const DirectionalArrowRight = createDirectionalIcon(FiArrowRight);
 * const DirectionalPlay = createDirectionalIcon(FiPlay, { shouldFlip: false });
 * ```
 */
export function createDirectionalIcon(
  IconComponent: React.ElementType,
  defaultProps?: Partial<DirectionalIconProps>
) {
  const DirectionalIconComponent = (props: DirectionalIconProps) => (
    <DirectionalIcon {...defaultProps} {...props} icon={IconComponent} />
  );

  DirectionalIconComponent.displayName = `Directional${(IconComponent as React.ComponentType & { displayName?: string; name?: string }).displayName || (IconComponent as React.ComponentType & { displayName?: string; name?: string }).name || 'Icon'}`;

  return DirectionalIconComponent;
}

/**
 * Pre-configured directional icons for common navigation patterns
 */
export const DirectionalIcons = {
  // Navigation arrows
  ArrowLeft: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),
  ArrowRight: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12H19M19 12L12 5M19 12L12 19" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),

  // Chevrons
  ChevronLeft: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18L9 12L15 6" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),
  ChevronRight: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18L15 12L9 6" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),

  // Double chevrons
  ChevronsLeft: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 17L6 12L11 7M18 17L13 12L18 7" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),
  ChevronsRight: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 17L18 12L13 7M6 17L11 12L6 7" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),

  // Back/Forward
  Back: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M5 12L12 19M5 12L12 5" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),
  Forward: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12H19M19 12L12 5M19 12L12 19" />
        </svg>
      )}
      shouldFlip
      {...props}
    />
  ),

  // Non-flipping icons
  Play: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5V19L19 12L8 5Z" />
        </svg>
      )}
      shouldFlip={false}
      {...props}
    />
  ),
  Pause: (props: Omit<DirectionalIconProps, 'icon'>) => (
    <DirectionalIcon
      icon={() => (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 4H10V20H6V4ZM14 4H18V20H14V4Z" />
        </svg>
      )}
      shouldFlip={false}
      {...props}
    />
  ),
};
